import type { Account, JobSpec } from "@eai-toolkit/client";
import { ChevronLeft, ErrorOutline, Refresh } from "@mui/icons-material";
import {
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tracker } from "@openreplay/tracker";
import { dump as yamlDump } from "js-yaml";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { JSX } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  BackButton,
  SidePadding,
  StickyFooter,
  StyledForm,
  StyledHeader,
  SubmitArea,
  SubmitButton,
  SubmitError,
} from "@components/FormStepper/FormLayout";
import FormStepper from "@components/FormStepper/FormStepper";
import {
  useValidationContextValue,
  ValidationContext,
  ValidationTextField,
} from "@components/ValidationContext/ValidationContext";

import DescriptiveTooltip from "../../../components/DescriptiveTooltip";
import { JOBS_EVENTS } from "../../../helpers/events";
import { useGetJobByIdQuery } from "../../../store/api/jobsApi";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useDataObjectsWithMap } from "../../../store/hooks/useDataObjectsWithMap";
import {
  editNewJobAccount,
  editNewJobSpec,
  regenerateNewJobName,
  selectNewJobForm,
  submitNewJob,
  swapNewJobFormState,
  validateNewJobFormState,
} from "../../../store/slices/newJobFormSlice";
import AccountField from "../components/AccountField";
import CommandField from "../components/CommandField";
import DataMountsField from "../components/DataMountsField";
import EnvironmentVarsField from "../components/EnvironmentVarsField";
import ImageField from "../components/ImageField";
import InternalDnsField from "../components/InternalDnsField";
import {
  JobFormBody,
  JobFormFooter,
  wideFieldStyle,
} from "../components/NewJobFormLayout";
import type { DnsConfig, TentativeJobSpec } from "../utils";
import {
  jobToTentativePayload,
  parseDockerImage,
  pathHelpText,
  stringifyDockerImage,
  tentativePayloadToJobSpec,
  validateJobName,
  validateWorkDir,
} from "../utils";

type SchedulingPolicyOption = {
  label: string;
  description: JSX.Element;
  jobSpecUpdate: Partial<TentativeJobSpec>;
};

const schedulingPolicyOptions: SchedulingPolicyOption[] = [
  {
    label: "Standard",
    description: (
      <>
        Your job will be <strong>preemptable</strong> and{" "}
        <strong>restartable</strong>. It may be interrupted if its resources are
        needed by another job on the cluster, but it will automatically restart
        as soon as possible.
      </>
    ),
    jobSpecUpdate: {
      preemptable: true,
      restartable: true,
      interactive: undefined,
    },
  },
  {
    label: "Preemptable",
    description: (
      <>
        Your job will be <strong>preemptable</strong>. If it is interrupted, you
        will have the option to manually restart it, but it will not restart
        automatically.
      </>
    ),
    jobSpecUpdate: {
      preemptable: true,
      restartable: false,
      interactive: undefined,
    },
  },
  {
    label: "Non-Preemptable",
    description: (
      <>
        Your job will not be interrupted until the max running time threshold of
        48 hours is hit, but it may take longer to queue, since fewer cluster
        resources are allocated to non-preemptable jobs.
      </>
    ),
    jobSpecUpdate: {
      preemptable: false,
      restartable: false,
      interactive: undefined,
    },
  },
];
function getSchedulingPolicyOptionIndex(jobSpec: TentativeJobSpec) {
  if (jobSpec.restartable) {
    return 0;
  }
  if (jobSpec.preemptable) {
    return 1;
  }
  return 2;
}

function normalizeDockerHubImage(image: string | undefined): string {
  if (!image) return "";

  const parsed = parseDockerImage(image);
  // ImageField expects just an empty string for a docker.io image, it's considered the default
  if (parsed.registry === "docker.io") {
    return stringifyDockerImage({
      registry: "",
      repository: parsed.repository,
      tag: parsed.tag,
    });
  }
  return image;
}

/**
 * Shared 3-column grid so CPUs↔GPUs and RAM↔GPU memory stay column-aligned
 * across both Resources rows. Uses 1fr (not var(--col)) so the pair fits
 * within the form's var(--row) cap.
 */
const ResourcesRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  columnGap: theme.spacing(1),
  rowGap: theme.spacing(1.5),
  paddingBottom: theme.spacing(2),
  width: "100%",
  maxWidth: "var(--row)",
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "&:not(:first-of-type)": {
    marginTop: theme.spacing(3),
  },
}));

const SpecPreviewContainer = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: "var(--row)",
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  // Prevent long unbreakable strings (e.g. file paths) inside the yaml
  // from forcing the whole form wider than the viewport.
  minWidth: 0,
  overflow: "hidden",
}));

const SpecPreviewHeader = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  margin: -1,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  padding: theme.spacing(0.5, 2),
}));

const SpecPreview = styled("pre")(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  overflowX: "auto",
  fontFamily: '"Courier New", monospace',
}));

const SchedulingOptionCard = styled(Paper)<{ selected?: boolean }>(
  ({ theme, selected }) => ({
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: selected
      ? theme.palette.background.paper
      : theme.palette.background.default,
    cursor: "pointer",
  }),
);

const SchedulingOptions = styled(RadioGroup)(({ theme }) => ({
  width: "100%",
  maxWidth: "var(--row)",
  marginBottom: theme.spacing(1),
}));

const SchedulingOptionLabel = styled(FormControlLabel)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const SchedulingOptionDescription = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(3.5),
}));

/** Inline hint following a SectionSubtitle (e.g. "(optional — ...)"). */
const SectionSubtitleHint = styled("span")(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  marginLeft: theme.spacing(1),
}));

const WorkDirField = styled(ValidationTextField)(({ theme }) => ({
  ...wideFieldStyle,
  display: "block",
  paddingBottom: theme.spacing(2),
}));

type LocationState =
  | {
      canGoBack?: boolean;
      fromId?: string;
      persistedNewJobFormState?: unknown;
    }
  | undefined;

function NewJobForm() {
  const dispatch = useAppDispatch();
  const newJob = useAppSelector(selectNewJobForm);

  const { dataObjectsMap } = useDataObjectsWithMap();
  // Resolved by AccountField's own by-id(-or-fullname) fetch; used here only
  // for display (overview label, DNS URL preview) so this page doesn't need
  // its own eager fetch of every account.
  const [resolvedAccount, setResolvedAccount] = useState<Account | null>(null);

  const handleAccountUpdate = useCallback(
    (accountId: string | null) => {
      dispatch(editNewJobAccount({ accountId: accountId ?? null }));
    },
    [dispatch],
  );

  const navigate = useNavigate();
  const location = useLocation();

  const locationState = location.state as LocationState;

  const prevJobState = useRef(newJob.state);
  useEffect(() => {
    const prev = prevJobState.current;
    prevJobState.current = newJob.state;
    if (newJob.state === "SUBMITTING" && prev !== "SUBMITTING") {
      tracker.event(JOBS_EVENTS.CREATE_FORM_SUBMITTED);
    } else if (newJob.state === "STATUS_POLLING" && prev === "SUBMITTING") {
      tracker.event(JOBS_EVENTS.CREATE_FORM_SUCCEEDED);
    } else if (
      newJob.state === "ERROR_AFTER_SUBMIT" &&
      prev !== "ERROR_AFTER_SUBMIT"
    ) {
      tracker.event(JOBS_EVENTS.CREATE_FORM_FAILED);
    }
  }, [newJob.state, newJob.responseId]);

  useEffect(() => {
    // ensure form state is updated and persisted in history before page unloads
    function blurFocusedElement() {
      if (
        document.activeElement &&
        document.activeElement instanceof HTMLElement
      ) {
        document.activeElement.blur();
      }
    }
    window.addEventListener("beforeunload", blurFocusedElement);
    return () => {
      window.removeEventListener("beforeunload", blurFocusedElement);
    };
  }, []);

  const fromId =
    locationState?.fromId ||
    new URLSearchParams(location.search.slice(1)).get("from-id");
  const [persistedNewJobFormState] = useState<
    ReturnType<typeof validateNewJobFormState>
  >(() => {
    if (locationState?.persistedNewJobFormState) {
      return validateNewJobFormState(locationState.persistedNewJobFormState);
    }
    return undefined;
  });

  const [waitingForPersistedFormState, setWaitingForPersistedFormState] =
    useState(Boolean(persistedNewJobFormState));

  useLayoutEffect(() => {
    if (waitingForPersistedFormState) {
      dispatch(
        swapNewJobFormState({
          formState: persistedNewJobFormState as NonNullable<
            typeof persistedNewJobFormState
          >,
        }),
      );
      setWaitingForPersistedFormState(false);
    }
  }, [waitingForPersistedFormState, persistedNewJobFormState, dispatch]);

  const [waitingForTemplate, setWaitingForTemplate] = useState(
    Boolean(
      fromId &&
      newJob.state === "READY_FOR_NEW_ENTRY" &&
      !persistedNewJobFormState,
    ),
  );

  useEffect(() => {
    if (!waitingForTemplate) {
      navigate(location.pathname + location.search, {
        replace: true,
        state: {
          ...locationState,
          persistedNewJobFormState: newJob,
        },
      });
    }
    // Don't include the location, locationState dependencies because those change every time, so it would cause an infinite loop
    // eslint-disable-next-line
  }, [waitingForTemplate, newJob, navigate]);

  // Fetch template job using RTK Query
  const { data: templateJobData, error: templateJobQueryError } =
    useGetJobByIdQuery(
      { jobId: fromId || "" },
      { skip: !fromId || !waitingForTemplate },
    );
  const templateJob = templateJobData?.data;

  useEffect(() => {
    if (waitingForTemplate && templateJob) {
      dispatch(
        editNewJobSpec({
          update: jobToTentativePayload(templateJob),
        }),
      );

      // Copy account from template job because the account doesn't get copied over in the jobToTentativePayload function.
      // templateJob.account may be an ID or a fullName — AccountField resolves either.
      const accountId = templateJob.account || null;
      dispatch(editNewJobAccount({ accountId }));

      setWaitingForTemplate(false);
    }
  }, [waitingForTemplate, templateJob, dispatch]);

  const templateJobError = waitingForTemplate
    ? templateJobQueryError
    : undefined;
  const templateJobLoading = waitingForTemplate && !templateJobError;

  const jobSpec: TentativeJobSpec & {
    resources: NonNullable<TentativeJobSpec["resources"]>;
    image: NonNullable<TentativeJobSpec["image"]>;
  } = useMemo(
    () => ({
      // NOTE: the below values are defaults shown in the UI but will not be
      // passed into the job spec; therefore they should match the default
      // values for job submission to the API
      image: "ubuntu:18.04",
      resources: {
        cpu: 1,
        gpu: 0,
        mem: 1,
      },
      ...newJob.tentativePayload,
    }),
    [newJob.tentativePayload],
  );

  const yamlSpec = useMemo(() => {
    const spec = tentativePayloadToJobSpec(
      newJob.tentativePayload,
      dataObjectsMap,
    );
    return Object.keys(spec).length
      ? yamlDump(spec, {
          sortKeys: true,
          // skipInvalid silently ignores 'undefined' values
          skipInvalid: true,
        })
      : "Nothing defined yet";
  }, [newJob.tentativePayload, dataObjectsMap]);

  const [step, setStep] = useState(0);

  const resourceValueUpdater = useCallback(
    (key: keyof NonNullable<JobSpec["resources"]>) => {
      return (e: React.FocusEvent<HTMLInputElement>) => {
        dispatch(
          editNewJobSpec({
            update: {
              resources: {
                ...jobSpec.resources,
                [key]: e.target.value ? Number(e.target.value) : undefined,
              },
            },
          }),
        );
      };
    },
    [jobSpec.resources, dispatch],
  );

  const updateCpu = useMemo(
    () => resourceValueUpdater("cpu"),
    [resourceValueUpdater],
  );
  const updateMem = useMemo(
    () => resourceValueUpdater("mem"),
    [resourceValueUpdater],
  );
  const updateGpu = useMemo(
    () => resourceValueUpdater("gpu"),
    [resourceValueUpdater],
  );
  const updateGpuMem = useMemo(
    () => resourceValueUpdater("gpuMem"),
    [resourceValueUpdater],
  );

  const dnsConfig = jobSpec.internalDns;

  const updateDnsConfig = useCallback(
    (config: DnsConfig | undefined) => {
      dispatch(editNewJobSpec({ update: { internalDns: config } }));
    },
    [dispatch],
  );

  const updateGpuModel = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const trimmed = e.target.value.trim();
      dispatch(
        editNewJobSpec({
          update: {
            resources: {
              ...jobSpec.resources,
              gpuModel: trimmed || undefined,
            },
          },
        }),
      );
    },
    [jobSpec.resources, dispatch],
  );

  const [gpuMemAllowed, setGpuMemAllowed] = useState(
    Boolean(jobSpec.resources.gpu),
  );
  useEffect(() => {
    setGpuMemAllowed(Boolean(jobSpec.resources.gpu));
  }, [jobSpec.resources.gpu]);

  const setSelectedSchedulingPolicyOption = useCallback(
    (selectedSchedulingPolicyOption: number) => {
      dispatch(
        editNewJobSpec({
          update:
            schedulingPolicyOptions[selectedSchedulingPolicyOption]
              .jobSpecUpdate,
        }),
      );
    },
    [dispatch],
  );
  const selectedSchedulingPolicyOption =
    getSchedulingPolicyOptionIndex(jobSpec);

  const workDirError = validateWorkDir(jobSpec.workdir) ?? null;
  const nameError = validateJobName(jobSpec.name) ?? null;
  const { cpu, mem, gpu, gpuMem } = jobSpec.resources;
  const cpuError = cpu === 0 ? "CPUs should be greater than 0" : null;
  const memError =
    mem && !Number.isInteger(mem) ? "RAM must be whole number" : null;
  const gpuError =
    gpu && !Number.isInteger(gpu) ? "GPUs must be whole number" : null;
  const gpuMemError =
    gpuMem && !Number.isInteger(gpuMem)
      ? "GPU memory must be whole number"
      : null;

  const {
    validationContextValue: globalValidationContextValue,
    validateAllFieldsInSection,
  } = useValidationContextValue();

  const scrollToErrorsAfterUpdate = useRef(false);
  // eslint-disable-next-line
  useEffect(() => {
    if (scrollToErrorsAfterUpdate.current) {
      let scrollTimeoutId: ReturnType<typeof setTimeout> | undefined;
      // wait for DOM to update with validation indications
      const animationFrame = requestAnimationFrame(() => {
        scrollToErrorsAfterUpdate.current = false;
        // find the first step index with errors (see FormStepper)
        const stepWithErrors = document.querySelector("[data-has-errors]");
        if (stepWithErrors) {
          const indexAttribute = stepWithErrors.getAttribute("data-step-index");
          if (indexAttribute) {
            const index = Number(indexAttribute);
            setStep(index);
            // Wait for the step's MUI Collapse (~250ms) to finish before
            // measuring; otherwise scrollIntoView targets a mid-animation
            // position and the error can land off-screen.
            scrollTimeoutId = setTimeout(() => {
              const firstError =
                stepWithErrors.querySelector('[aria-invalid="true"]') ??
                stepWithErrors;
              firstError.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 300);
          }
        }
      });
      return () => {
        cancelAnimationFrame(animationFrame);
        if (scrollTimeoutId !== undefined) clearTimeout(scrollTimeoutId);
      };
    }
  });

  return (
    <StyledForm
      noValidate
      onKeyDown={(e) => {
        // Prevent Enter in a text field from submitting the form prematurely.
        if (
          e.key === "Enter" &&
          e.target instanceof HTMLInputElement &&
          e.target.type !== "submit"
        ) {
          e.preventDefault();
        }
      }}
      onSubmit={(e) => {
        e.preventDefault();
        if (globalValidationContextValue.errors.size === 0) {
          dispatch(submitNewJob({ dataObjectsMap }));
        } else {
          scrollToErrorsAfterUpdate.current = true;
          validateAllFieldsInSection();
        }
      }}
    >
      <SidePadding>
        <JobFormBody>
          <StyledHeader>
            <Typography variant="h2">Launch a new job</Typography>
            {locationState?.canGoBack && (
              <BackButton type="button" onClick={() => navigate(-1)}>
                <ChevronLeft />
              </BackButton>
            )}
          </StyledHeader>
          {templateJobError ? (
            <Typography variant="body1" color="error">
              Error loading template job
            </Typography>
          ) : templateJobLoading ? (
            <Typography variant="body1">Loading template job...</Typography>
          ) : waitingForPersistedFormState ? null : (
            <ValidationContext.Provider value={globalValidationContextValue}>
              <FormStepper
                nonLinear
                activeStep={step}
                onStepSelect={setStep}
                activeIndex={step}
              >
                {[
                  {
                    label: "Execution context",
                    overviewItems: [
                      jobSpec.image || "No image selected",
                      `${jobSpec.data?.length || 0} data mounts`,
                      `${
                        jobSpec.environmentVars?.length || 0
                      } environment variables`,
                      `${
                        jobSpec.workdir ? "Custom" : "Default"
                      } working directory`,
                      `${jobSpec.command ? "Custom" : "Default"} command`,
                    ],
                    children: (
                      <>
                        <SectionSubtitle variant="subtitle1">
                          Docker image
                        </SectionSubtitle>
                        <ImageField
                          value={normalizeDockerHubImage(jobSpec.image || "")}
                          onUpdate={(image) =>
                            dispatch(
                              editNewJobSpec({
                                update: { image: image ?? undefined },
                              }),
                            )
                          }
                          submitted={newJob.state === "SUBMITTING"}
                        />
                        <SectionSubtitle variant="subtitle1">
                          Data mounts
                        </SectionSubtitle>
                        <DataMountsField
                          values={jobSpec.data ?? []}
                          onUpdate={(dataIds) =>
                            dispatch(
                              editNewJobSpec({ update: { data: dataIds } }),
                            )
                          }
                        />
                        <SectionSubtitle variant="subtitle1">
                          Environment variables
                        </SectionSubtitle>
                        <EnvironmentVarsField
                          values={jobSpec.environmentVars ?? []}
                          onUpdate={(environmentVars) =>
                            dispatch(
                              editNewJobSpec({ update: { environmentVars } }),
                            )
                          }
                        />
                        <SectionSubtitle variant="subtitle1">
                          What to run
                        </SectionSubtitle>
                        <WorkDirField
                          instanceId="workdir"
                          fullWidth
                          label="Working directory"
                          variant="outlined"
                          placeholder="use default"
                          InputLabelProps={{ shrink: true }}
                          defaultValue={jobSpec.workdir}
                          valueForValidation={jobSpec.workdir ?? ""}
                          onBlur={(e) => {
                            dispatch(
                              editNewJobSpec({
                                update: { workdir: e.target.value },
                              }),
                            );
                          }}
                          error={workDirError}
                          helperText={pathHelpText}
                        />
                        <CommandField
                          defaultValue={jobSpec.command ?? ""}
                          onUpdate={(command) =>
                            dispatch(editNewJobSpec({ update: { command } }))
                          }
                        />
                      </>
                    ),
                  },
                  {
                    label: "Resources",
                    overviewItems: [
                      `${jobSpec.resources.cpu ?? 1} CPUs`,
                      `${jobSpec.resources.mem ?? 1}GB RAM`,
                      `${jobSpec.resources.gpu ?? 0} GPUs`,
                      `${
                        jobSpec.resources.gpuMem
                          ? `${jobSpec.resources.gpuMem}GB`
                          : "Any amount of"
                      } GPU memory`,
                      ...(jobSpec.resources.gpuModel
                        ? [`GPU model: ${jobSpec.resources.gpuModel}`]
                        : []),
                    ],
                    children: (
                      <>
                        <SectionSubtitle variant="subtitle1">
                          Resource options
                        </SectionSubtitle>

                        <ResourcesRow>
                          <ValidationTextField
                            instanceId="cpu"
                            variant="outlined"
                            fullWidth
                            label="CPUs"
                            type="number"
                            required
                            inputProps={{ min: 0 }}
                            onBlur={updateCpu}
                            defaultValue={jobSpec.resources.cpu}
                            valueForValidation={jobSpec.resources.cpu ?? 1}
                            error={cpuError}
                          />
                          <ValidationTextField
                            instanceId="mem"
                            variant="outlined"
                            fullWidth
                            label="RAM (GB)"
                            type="number"
                            required
                            inputProps={{ min: 1, step: 1 }}
                            onBlur={updateMem}
                            defaultValue={jobSpec.resources.mem}
                            valueForValidation={jobSpec.resources.mem ?? 1}
                            error={memError}
                          />
                        </ResourcesRow>

                        <ResourcesRow>
                          <ValidationTextField
                            instanceId="gpu"
                            variant="outlined"
                            fullWidth
                            label="GPUs"
                            type="number"
                            required
                            inputProps={{ min: 0, step: 1 }}
                            onBlur={updateGpu}
                            onChange={(e) =>
                              setGpuMemAllowed(
                                Boolean(
                                  e.target.value && Number(e.target.value),
                                ),
                              )
                            }
                            defaultValue={jobSpec.resources.gpu}
                            valueForValidation={jobSpec.resources.gpu ?? 0}
                            error={gpuError}
                          />
                          <DescriptiveTooltip
                            title={
                              gpuMemAllowed ? "" : "GPUs must be specified"
                            }
                          >
                            <ValidationTextField
                              instanceId="gpumem"
                              variant="outlined"
                              fullWidth
                              label="GPU memory (GB)"
                              type="number"
                              disabled={!gpuMemAllowed}
                              inputProps={{ min: 12, max: 32, step: 1 }}
                              placeholder="don't care"
                              InputLabelProps={{ shrink: true }}
                              onBlur={updateGpuMem}
                              defaultValue={jobSpec.resources.gpuMem}
                              valueForValidation={
                                jobSpec.resources.gpuMem ?? ""
                              }
                              error={gpuMemError}
                            />
                          </DescriptiveTooltip>
                          <div>
                            <DescriptiveTooltip
                              title={
                                gpuMemAllowed ? "" : "GPUs must be specified"
                              }
                            >
                              <TextField
                                key={jobSpec.resources.gpuModel ?? ""}
                                variant="outlined"
                                fullWidth
                                label="GPU model"
                                disabled={!gpuMemAllowed}
                                placeholder="don't care"
                                InputLabelProps={{ shrink: true }}
                                helperText="e.g. a100 or h100 or p100. Prefix with '!' to exclude specific models (e.g. !a100)."
                                defaultValue={jobSpec.resources.gpuModel ?? ""}
                                onBlur={updateGpuModel}
                              />
                            </DescriptiveTooltip>
                          </div>
                        </ResourcesRow>
                      </>
                    ),
                  },
                  {
                    label: "Scheduling",
                    overviewItems: [
                      schedulingPolicyOptions[selectedSchedulingPolicyOption]
                        .label,
                    ],
                    children: (
                      <>
                        <SectionSubtitle variant="subtitle1">
                          Choose the scheduling policy that best suits your job
                        </SectionSubtitle>
                        <FormControl>
                          <SchedulingOptions
                            value={selectedSchedulingPolicyOption}
                            onChange={(e) =>
                              setSelectedSchedulingPolicyOption(
                                Number(e.target.value),
                              )
                            }
                          >
                            {schedulingPolicyOptions.map(
                              ({ label, description }, index) => (
                                <SchedulingOptionCard
                                  variant="outlined"
                                  key={label}
                                  selected={
                                    index === selectedSchedulingPolicyOption
                                  }
                                  onClick={() =>
                                    setSelectedSchedulingPolicyOption(index)
                                  }
                                >
                                  <SchedulingOptionLabel
                                    value={index}
                                    control={<Radio color="primary" />}
                                    label={
                                      <Typography variant="subtitle1">
                                        {label}
                                      </Typography>
                                    }
                                  />
                                  <SchedulingOptionDescription variant="body2">
                                    {description}
                                  </SchedulingOptionDescription>
                                </SchedulingOptionCard>
                              ),
                            )}
                          </SchedulingOptions>
                        </FormControl>
                      </>
                    ),
                  },
                  {
                    label: "Identification",
                    overviewItems: [
                      jobSpec.name || jobSpec.randomlyGeneratedName,
                    ],
                    children: (
                      <>
                        <SectionSubtitle variant="subtitle1">
                          Change the job name (or stick with the generated one!)
                        </SectionSubtitle>
                        <ValidationTextField
                          instanceId="jobname"
                          fullWidth
                          label="Name"
                          variant="outlined"
                          placeholder={jobSpec.randomlyGeneratedName}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            endAdornment: (
                              <IconButton
                                onClick={() => dispatch(regenerateNewJobName())}
                                title="New random name"
                                disabled={Boolean(jobSpec.name)}
                              >
                                <Refresh />
                              </IconButton>
                            ),
                          }}
                          defaultValue={jobSpec.name}
                          // force defaultValue to reinitialize when the
                          // form submission state is updated (this helps us
                          // with incremented names on revision after failure)
                          key={newJob.state}
                          valueForValidation={jobSpec.name ?? ""}
                          onBlur={(e) => {
                            dispatch(
                              editNewJobSpec({
                                update: {
                                  name: e.target.value.toLowerCase(),
                                },
                              }),
                            );
                          }}
                          error={nameError}
                          sx={wideFieldStyle}
                        />
                      </>
                    ),
                    contentPaddingBottom: 2,
                  },
                  {
                    label: "Select account where job will live",
                    inputRequired: true,
                    overviewItems: [
                      resolvedAccount?.fullName ||
                        newJob.accountId ||
                        "No account selected",
                    ],
                    children: (
                      <AccountField
                        value={newJob.accountId || null}
                        onUpdate={handleAccountUpdate}
                        onAccountResolved={setResolvedAccount}
                      />
                    ),
                    contentPaddingBottom: 2,
                  },
                  {
                    label: "Internal DNS",
                    overviewItems: dnsConfig
                      ? [
                          `${dnsConfig.name || "(unnamed)"} (${
                            dnsConfig.ports.length
                          } port${dnsConfig.ports.length === 1 ? "" : "s"})`,
                        ]
                      : ["None selected"],
                    children: (
                      <>
                        <SectionSubtitle variant="subtitle1">
                          Internal DNS
                          <SectionSubtitleHint>
                            (optional — for in-cluster service discovery)
                          </SectionSubtitleHint>
                        </SectionSubtitle>
                        <InternalDnsField
                          value={dnsConfig}
                          onUpdate={updateDnsConfig}
                          accountId={resolvedAccount?.id ?? null}
                          replicas={jobSpec.resources?.replicas}
                        />
                      </>
                    ),
                  },
                  {
                    label: "Review job spec",
                    children: (
                      <SpecPreviewContainer variant="outlined" tabIndex={0}>
                        <SpecPreviewHeader variant="outlined">
                          jobSpec.yaml
                        </SpecPreviewHeader>
                        <SpecPreview>
                          <code>{yamlSpec}</code>
                        </SpecPreview>
                      </SpecPreviewContainer>
                    ),
                  },
                ]}
              </FormStepper>
            </ValidationContext.Provider>
          )}
        </JobFormBody>
      </SidePadding>
      <StickyFooter variant="elevation">
        <JobFormFooter>
          <Button
            type="button"
            color="primary"
            {...(locationState?.canGoBack
              ? {
                  onClick: () => {
                    tracker.event(JOBS_EVENTS.CREATE_FORM_BACK_CLICKED);
                    navigate(-1);
                  },
                }
              : {
                  // go to the homepage if we don't have anywhere to go back to
                  component: "a",
                  href: "/",
                  onClick: () =>
                    tracker.event(JOBS_EVENTS.CREATE_FORM_BACK_CLICKED),
                })}
          >
            Cancel
          </Button>
          <SubmitArea>
            {newJob.error && newJob.state !== "ERROR_AFTER_SUBMIT" && (
              <SubmitError variant="body1" color="error">
                <ErrorOutline />
                {newJob.error}
              </SubmitError>
            )}
            <SubmitButton
              type="submit"
              color="primary"
              variant="contained"
              disabled={newJob.state === "SUBMITTING"}
            >
              Launch job
            </SubmitButton>
          </SubmitArea>
        </JobFormFooter>
      </StickyFooter>
    </StyledForm>
  );
}

export default NewJobForm;
